import React, { useState, useRef, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";

import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createSuccessFeedback,
  createErrorFeedback,
} from "../../../context/ui-feedback/action";

import {
  useSearchApps,
  useDeleteAppByKey,
  useRegistrateApp,
} from "../../../api/mobil-app-api";
import { PageLayout } from "../../common/page-layout/page-layout";
import { ControllerRow } from "../../common/controller-row/controller-row";
import { Pager } from "../../common/pager/pager";
import { MobileAppTable } from "./components/mobile-app-table";
import { RegistrationModal } from "./components/registration-modal";

export const MobileApps = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const localSearchUUID = useRef(null);
  const [errorInLoading, setErrorInLoading] = useState(false);
  const [registration, setRegistration] = useState(false);
  const [listQuery, setListQuery] = useState({
    limit: 10,
    offset: 0,
    allCount: null,
    results: null,
  });

  const searchApps = useSearchApps();
  const deleteAppByKey = useDeleteAppByKey();
  const registrateApp = useRegistrateApp();

  useEffect(() => {
    const loadApps = async () => {
      setErrorInLoading(false);
      setListQuery({
        ...listQuery,
        results: null,
      });
      try {
        const search = {
          limit: listQuery.limit,
          offset: listQuery.offset,
        };
        await searchApps(search, (data) => {
          if (data instanceof Error) {
            throw data;
          } else {
            if (data.applications.length === 0 && data.allCount > 0) {
              setListQuery({
                ...listQuery,
                offset:
                  (Math.ceil(data.allCount / listQuery.limit) - 1) *
                  listQuery.limit,
              });
              localSearchUUID.current = null;
            } else {
              setListQuery({
                ...listQuery,
                allCount: data.allCount,
                results: data.applications,
              });
            }
          }
        });
      } catch (ex) {
        if (ex.response?.status === 401 || ex.response?.status === 403) {
          navigate("/");
        } else {
          setErrorInLoading(true);
          createErrorFeedback(
            uiFeedbackDispatch,
            intl.formatMessage({
              id: "page.apps.exception.listapps.title",
            }),
            intl.formatMessage({
              id: "page.apps.exception.listapps.description",
            }),
          );
        }
      }
    };

    const checkNotEmpy = (items) => {
      return items.filter((item) => item === null).length === 0;
    };

    if (
      localSearchUUID.current === null &&
      checkNotEmpy([intl, listQuery, navigate, searchApps, uiFeedbackDispatch])
    ) {
      localSearchUUID.current = new Date().getTime();
      loadApps();
    }
  }, [
    localSearchUUID,
    listQuery.limit,
    listQuery.offset,
    intl,
    listQuery,
    navigate,
    searchApps,
    uiFeedbackDispatch,
  ]);

  const deleteApp = async (app) => {
    try {
      createWaiting(uiFeedbackDispatch);
      await deleteAppByKey(app.deviceKey, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage(
              { id: "page.apps.success.delete" },
              {
                name: app.user.name,
                key: app.deviceKey,
              },
            ),
          );
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({ id: "page.apps.exception.delete.title" }),
          intl.formatMessage(
            { id: "page.apps.exception.delete.description" },
            {
              name: app.user.name,
              key: app.deviceKey,
            },
          ),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
      localSearchUUID.current = null;
    }
  };

  const createAppRegistration = async (user) => {
    try {
      setRegistration(false);
      createWaiting(uiFeedbackDispatch);
      await registrateApp(user.id, (data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          createSuccessFeedback(
            uiFeedbackDispatch,
            intl.formatMessage(
              { id: "page.apps.success.registration" },
              {
                key: data.deviceKey,
              },
            ),
          );
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        createErrorFeedback(
          uiFeedbackDispatch,
          intl.formatMessage({ id: "page.apps.exception.registration.title" }),
          intl.formatMessage({
            id: "page.apps.exception.registration.description",
          }),
        );
      }
    } finally {
      closeWaiting(uiFeedbackDispatch);
      localSearchUUID.current = null;
    }
  };

  const changePage = (page) => {
    setListQuery({
      ...listQuery,
      offset: page * listQuery.limit,
    });
    localSearchUUID.current = null;
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <>
      <PageLayout
        paths={buildPaths()}
        title={intl.formatMessage({ id: "page.apps.title" })}
      >
        <>
          <ControllerRow>
            <div className="text-end mb-2">
              <Button
                variant="outline-primary"
                onClick={() => {
                  setRegistration(true);
                }}
              >
                <FormattedMessage id="page.apps.button.create" />
              </Button>
            </div>
          </ControllerRow>
          <Pager
            currentPage={listQuery.offset / listQuery.limit}
            allPage={Math.ceil(listQuery?.allCount / listQuery.limit)}
            topPager={true}
            bottomPager={true}
            handleChangePage={changePage}
          >
            <MobileAppTable
              rowNumberInit={listQuery.offset}
              apps={listQuery?.results}
              hasError={errorInLoading}
              handleDelete={(app) => {
                deleteApp(app);
              }}
            />
          </Pager>
        </>
      </PageLayout>
      {registration ? (
        <RegistrationModal
          handleClose={() => {
            setRegistration(false);
          }}
          handleOk={(user) => {
            createAppRegistration(user);
          }}
        />
      ) : null}
    </>
  );
};
